import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { PageSidesheetActions } from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { TableColumnTypes, TableConfig } from '@next/core-lib/table';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, ReplaySubject } from 'rxjs';
import { map, startWith, take, takeUntil } from 'rxjs/operators';

import { CropVarietySpecificNormFilter, PageTitles, SiteState, CropVarietySpecificNorm, CropVarietySpecificNormValue } from '../../../shared/models';
import { DisplayService } from '../../../shared/services/display.service';
import {
  AddCropVarietySpecificNorm,
  ClearCropVarietySpecificNormFilter,
  DeleteCropVarietySpecificNorm,
  EditCropVarietySpecificNorm,
  LoadCropVarietySpecificNormsRequest,
  OpenCropVarietySpecificNormsImportForm,
  UpdateCropVarietySpecificNormFilter,
} from '../../../store/actions';
import { getCropVarietySpecificNormFilter, getCropVarietySpecificNorms, getCropVarietySpecificNormTotalNumberOfItems } from '../../../store/selectors';

@Component({
  selector: 'app-crop-variety-specific-norm-list',
  templateUrl: './crop-variety-specific-norm-list.component.html',
  standalone: false,
})
export class CropVarietySpecificNormListComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);
  readonly displayService = inject(DisplayService);
  private readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);

  @ViewChild('paginator', { static: false }) paginator: MatPaginator;
  title = PageTitles.CropVarietySpecificNorm;
  tableConfig: TableConfig = {
    dataSource: new MatTableDataSource(),
    selectColumn: false,
    pagination: false,
    header: true,
    footer: false,
    disableCards: true,
    columns: [
      {
        id: 'calendarYear',
        label: this.locale.translate('year'),
        type: TableColumnTypes.NUMBER,
        disableFormatting: true,
      },
      {
        id: 'agrifirmCropId',
        label: this.locale.translate('columnHeader.agrifirmCropId'),
        type: TableColumnTypes.NUMBER,
        disableFormatting: true,
      },
      {
        id: 'cropName',
        label: this.locale.translate('cropName'),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'cropVarietyName',
        label: this.locale.translate('cropVarietyName'),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'cropVarietySpecificNormString',
        label: this.locale.translate('cropVarietySpecificNorm'),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'isNitrogenDifferentiationString',
        label: this.locale.translate('isNitrogenDifferentiation'),
        type: TableColumnTypes.STRING,
      },
    ],
    actions: [
      {
        id: 'edit',
        label: this.locale.translate('edit'),
        icon: 'edit',
        callback: (event: CropVarietySpecificNorm) => this.editNorm(event),
        disabled: () => false,
      },
      {
        id: 'delete',
        label: this.locale.translate('delete'),
        icon: 'delete',
        callback: (event: CropVarietySpecificNorm) => this.deleteNorm(event),
        disabled: () => false,
      },
    ],
  };
  filterControl = new FormControl();
  defaultPageSize = 10;
  pageSizeOptions = [10, 25, 50];

  request: { pageNumber: number; pageSize: number; filter: CropVarietySpecificNormFilter } = {
    pageNumber: 1,
    pageSize: 10,
    filter: {},
  };

  totalNumberOfItems$ = this.store.pipe(select(getCropVarietySpecificNormTotalNumberOfItems));

  norms$ = this.store.pipe(select(getCropVarietySpecificNorms));

  filterValue$ = this.filterControl.valueChanges.pipe(
    map((value: string) => value || ''),
    startWith(''),
  );

  filterEnabled$ = new BehaviorSubject(false);
  isMobile$ = this.breakpoint.ltLg$;
  isEmpty$ = combineLatest([this.filterEnabled$, this.totalNumberOfItems$]).pipe(
    map(([filterEnabled, totalNumberOfItems]) => !filterEnabled && totalNumberOfItems === 0),
  );

  cropVarietySpecificNormFilter$ = this.store.pipe(select(getCropVarietySpecificNormFilter));

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  ngOnInit() {
    this.norms$.pipe(takeUntil(this.destroyed$)).subscribe((norms) => {
      this.tableConfig.dataSource.data = norms.map((norm) => ({
        ...norm,
        cropVarietySpecificNormString: this.getCropVarietySpecificNormString(norm.cropVarietySpecificNorm),
        isNitrogenDifferentiationString: norm.isNitrogenDifferentiation ? this.locale.translate('yes') : '',
      }));
    });

    this.updateData();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  updateData() {
    this.store.dispatch(new LoadCropVarietySpecificNormsRequest(this.request.pageNumber, this.request.pageSize, this.request.filter));
  }

  updateFilter(data: CropVarietySpecificNormFilter) {
    this.request.filter = data;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.request.pageNumber = 1;
    this.store.dispatch(new UpdateCropVarietySpecificNormFilter(data));
    this.store.dispatch(new LoadCropVarietySpecificNormsRequest(this.request.pageNumber, this.request.pageSize, this.request.filter));
  }

  setFilterEnabled(isEnabled: boolean) {
    this.filterEnabled$.next(isEnabled);
  }

  resetFilter() {
    this.store.dispatch(new ClearCropVarietySpecificNormFilter());
  }

  onPage(event: PageEvent) {
    this.request = {
      ...this.request,
      pageNumber: event.pageIndex + 1,
      pageSize: event.pageSize,
    };
    this.updateData();
  }

  addNorm() {
    this.store.dispatch(new AddCropVarietySpecificNorm());
    if (this.paginator) {
      (this.paginator.length as number)++;
    }
  }

  editNorm(norm: CropVarietySpecificNorm) {
    this.store.dispatch(new EditCropVarietySpecificNorm(norm));
  }

  deleteNorm(norm: CropVarietySpecificNorm) {
    this.dialog
      .confirm({
        title: this.locale.translate('delete'),
        text: this.locale.translate('deleteCropVarietySpecificNorm'),
        pushHistoryState: false,
      })
      .pipe(take(1))
      .subscribe((x) => {
        if (x.status === DIALOG_RETURN_TYPES.confirm) {
          this.store.dispatch(new DeleteCropVarietySpecificNorm(norm));
          if (this.paginator) {
            this.paginator.pageIndex = 0;
            (this.paginator.length as number)--;
          }
          this.request.pageNumber = 1;
          this.updateData();
        }
      });
  }

  importCropVarietySpecificNorms() {
    this.store.dispatch(new OpenCropVarietySpecificNormsImportForm());
  }

  setSearch() {
    this.store.dispatch(PageSidesheetActions.openSidesheet());
  }

  getCropVarietySpecificNormString = (norm: CropVarietySpecificNormValue): string => this.displayService.getCropVarietySpecificNormString(norm);
}
